import { z } from 'zod'

export const AssemblyConnectionSchema = z.object({
  id: z.string(),
  fromPartId: z.string(),
  fromConnectorId: z.string(),
  toPartId: z.string(),
  toConnectorId: z.string(),
  jointType: z.enum(['fixed', 'revolute']),
})

export type AssemblyConnection = z.infer<typeof AssemblyConnectionSchema>

export function isC103MotorConnection(connection: AssemblyConnection) {
  return connection.fromConnectorId === 'motor-male-fork'
    || connection.toConnectorId === 'motor-male-fork'
    || connection.fromConnectorId === 'cross-male-fork'
    || connection.toConnectorId === 'cross-male-fork'
}

export function normalizeConnectionJointTypes(
  connections: AssemblyConnection[],
): AssemblyConnection[] {
  return connections.map((connection) =>
    isC103MotorConnection(connection) && connection.jointType !== 'revolute'
      ? { ...connection, jointType: 'revolute' }
      : connection)
}

export const battlebotMiniConnections: AssemblyConnection[] = [
  {
    id: 'battlebot-connection-c2-left',
    fromPartId: 'battlebot-c2-13',
    fromConnectorId: 'cross-male-a',
    toPartId: 'battlebot-rear-left-cube',
    toConnectorId: 'cross-female-back',
    jointType: 'fixed',
  },
  {
    id: 'battlebot-connection-c2-right',
    fromPartId: 'battlebot-c2-13',
    fromConnectorId: 'cross-male-b',
    toPartId: 'battlebot-rear-right-cube',
    toConnectorId: 'cross-female-back',
    jointType: 'fixed',
  },
  {
    id: 'battlebot-connection-c3-left',
    fromPartId: 'battlebot-c3-02',
    fromConnectorId: 'cross-male-pair-left',
    toPartId: 'battlebot-rear-left-cube',
    toConnectorId: 'cross-female-bottom',
    jointType: 'fixed',
  },
  {
    id: 'battlebot-connection-c3-right',
    fromPartId: 'battlebot-c3-02',
    fromConnectorId: 'cross-male-pair-right',
    toPartId: 'battlebot-rear-right-cube',
    toConnectorId: 'cross-female-bottom',
    jointType: 'fixed',
  },
  {
    id: 'battlebot-connection-c3-front',
    fromPartId: 'battlebot-c3-02',
    fromConnectorId: 'cross-male-single',
    toPartId: 'battlebot-front-cube',
    toConnectorId: 'cross-female-bottom',
    jointType: 'fixed',
  },
  {
    id: 'battlebot-connection-wheel-left',
    fromPartId: 'battlebot-left-wheel',
    fromConnectorId: 'motor-male-wheel',
    toPartId: 'battlebot-rear-left-cube',
    toConnectorId: 'motor-female-front',
    jointType: 'revolute',
  },
  {
    id: 'battlebot-connection-wheel-right',
    fromPartId: 'battlebot-right-wheel',
    fromConnectorId: 'motor-male-wheel',
    toPartId: 'battlebot-rear-right-cube',
    toConnectorId: 'motor-female-front',
    jointType: 'revolute',
  },
  {
    id: 'battlebot-connection-fork-motor',
    fromPartId: 'battlebot-c1-03',
    fromConnectorId: 'motor-male-fork',
    toPartId: 'battlebot-front-cube',
    toConnectorId: 'motor-female-front',
    jointType: 'revolute',
  },
  {
    id: 'battlebot-connection-fork-cross',
    fromPartId: 'battlebot-c1-03',
    fromConnectorId: 'cross-male-fork',
    toPartId: 'battlebot-front-cube',
    toConnectorId: 'cross-female-back',
    jointType: 'revolute',
  },
  {
    id: 'battlebot-connection-foot',
    fromPartId: 'battlebot-c1-19',
    fromConnectorId: 'cross-male-foot',
    toPartId: 'battlebot-front-cube',
    toConnectorId: 'cross-female-right',
    jointType: 'fixed',
  },
]

export const battlebotConnections: AssemblyConnection[] = battlebotMiniConnections.flatMap(
  (connection) => {
    if (connection.id === 'battlebot-connection-wheel-left') {
      return [
        {
          id: 'battlebot-connection-gearbox-left-input',
          fromPartId: 'battlebot-left-md-01',
          fromConnectorId: 'motor-male-input',
          toPartId: 'battlebot-rear-left-cube',
          toConnectorId: 'motor-female-front',
          jointType: 'fixed' as const,
        },
        {
          ...connection,
          toPartId: 'battlebot-left-md-01',
          toConnectorId: 'motor-female-output',
        },
      ]
    }
    if (connection.id === 'battlebot-connection-wheel-right') {
      return [
        {
          id: 'battlebot-connection-gearbox-right-input',
          fromPartId: 'battlebot-right-md-01',
          fromConnectorId: 'motor-male-input',
          toPartId: 'battlebot-rear-right-cube',
          toConnectorId: 'motor-female-front',
          jointType: 'fixed' as const,
        },
        {
          ...connection,
          toPartId: 'battlebot-right-md-01',
          toConnectorId: 'motor-female-output',
        },
      ]
    }
    return [connection]
  },
)
